/**
 * 인증이 필요한 REST API용 axios 인스턴스 팩토리.
 * - 요청 인터셉터: 매 요청마다 Access Token을 자동으로 authorization 헤더에 삽입
 * - 응답 인터셉터: 401 시 tokenRefreshManager를 통해 토큰 갱신 후 원래 요청 재시도
 */
import axios from 'axios';
import Cookies from 'js-cookie';
import { LOGIN_AUTH_TOKEN_KEY } from '../constants/token';
import { refreshAccessToken, handleRefreshFailure } from './tokenRefreshManager';

export function createAuthAxios(baseURL: string) {
  const instance = axios.create({ baseURL });

  // 요청 인터셉터: Access Token 자동 삽입
  instance.interceptors.request.use((config) => {
    const token = Cookies.get(LOGIN_AUTH_TOKEN_KEY);
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  });

  // 응답 인터셉터: 401 시 토큰 갱신 후 재시도 (_retry 플래그로 무한 루프 방지)
  // 단, Access Token 자체가 없으면 "미로그인 상태"이므로 refresh를 시도하지 않는다.
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (!Cookies.get(LOGIN_AUTH_TOKEN_KEY)) return Promise.reject(error);

        originalRequest._retry = true;
        const result = await refreshAccessToken();
        if (result.success) {
          originalRequest.headers.authorization = `Bearer ${result.token}`;
          return instance(originalRequest);
        }
        if (result.reason === 'auth') {
          handleRefreshFailure();
        }
        // transient 실패: 로그아웃 없이 원래 에러를 reject
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

/**
 * fetch 등 axios 외 HTTP 호출에서 인증 헤더를 수동으로 삽입할 때 사용.
 * createAuthAxios 인스턴스를 사용하는 경우에는 불필요.
 */
export function getAuthHeaders(): Record<string, string> {
  const token = Cookies.get(LOGIN_AUTH_TOKEN_KEY);
  return token ? { authorization: `Bearer ${token}` } : {};
}
